export type Polarization = 'horizontal' | 'vertical' | 'circular';

interface CurveCoefficients { a: number[]; b: number[]; c: number[]; slope: number; intercept: number }

const kHorizontal: CurveCoefficients = {
  a: [-5.33980, -0.35351, -0.23789, -0.94158],
  b: [-0.10008, 1.26970, 0.86036, 0.64552],
  c: [1.13098, 0.45400, 0.15354, 0.16817],
  slope: -0.18961,
  intercept: 0.71147,
};
const kVertical: CurveCoefficients = {
  a: [-3.80595, -3.44965, -0.39902, 0.50167],
  b: [0.56934, -0.22911, 0.73042, 1.07319],
  c: [0.81061, 0.51059, 0.11899, 0.27195],
  slope: -0.16398,
  intercept: 0.63297,
};
const alphaHorizontal: CurveCoefficients = {
  a: [-0.14318, 0.29591, 0.32177, -5.37610, 16.1721],
  b: [1.82442, 0.77564, 0.63773, -0.96230, -3.29980],
  c: [-0.55187, 0.19822, 0.13164, 1.47828, 3.43990],
  slope: 0.67849,
  intercept: -1.95537,
};
const alphaVertical: CurveCoefficients = {
  a: [-0.07771, 0.56727, -0.20238, -48.2991, 48.5833],
  b: [2.33840, 0.95545, 1.14520, 0.791669, 0.791459],
  c: [-0.76284, 0.54039, 0.26809, 0.116226, 0.116479],
  slope: -0.053739,
  intercept: 0.83433,
};

function evaluateCurve(frequencyGHz: number, coefficients: CurveCoefficients, logarithmicOutput: boolean): number {
  const logFrequency = Math.log10(frequencyGHz);
  let value = coefficients.slope * logFrequency + coefficients.intercept;
  for (let index = 0; index < coefficients.a.length; index += 1) {
    const a = coefficients.a[index];
    const b = coefficients.b[index];
    const c = coefficients.c[index];
    if (a === undefined || b === undefined || c === undefined) continue;
    value += a * Math.exp(-1 * (((logFrequency - b) / c) ** 2));
  }
  return logarithmicOutput ? 10 ** value : value;
}

export interface RainSpecificAttenuationInput {
  frequencyGHz: number;
  rainRateMmPerHour: number;
  polarization?: Polarization;
  pathElevationDeg?: number;
  polarizationTiltDeg?: number;
}

export interface RainSpecificAttenuation {
  specificAttenuationDbPerKm: number;
  k: number;
  alpha: number;
  model: 'ITU-R P.838-3';
}

export function rainSpecificAttenuation(input: RainSpecificAttenuationInput): RainSpecificAttenuation {
  if (input.frequencyGHz < 1 || input.frequencyGHz > 1_000) throw new RangeError('ITU-R P.838-3 supports 1–1000 GHz');
  if (input.rainRateMmPerHour < 0) throw new RangeError('Rain rate cannot be negative');
  const kH = evaluateCurve(input.frequencyGHz, kHorizontal, true);
  const kV = evaluateCurve(input.frequencyGHz, kVertical, true);
  const alphaH = evaluateCurve(input.frequencyGHz, alphaHorizontal, false);
  const alphaV = evaluateCurve(input.frequencyGHz, alphaVertical, false);
  const theta = ((input.pathElevationDeg ?? 0) * Math.PI) / 180;
  const defaultTilt = input.polarization === 'vertical' ? 90 : input.polarization === 'circular' ? 45 : 0;
  const tau = ((input.polarizationTiltDeg ?? defaultTilt) * Math.PI) / 180;
  const polarizationFactor = Math.cos(theta) ** 2 * Math.cos(2 * tau);
  const k = (kH + kV + (kH - kV) * polarizationFactor) / 2;
  const alpha = (kH * alphaH + kV * alphaV + (kH * alphaH - kV * alphaV) * polarizationFactor) / (2 * k);
  return { specificAttenuationDbPerKm: k * input.rainRateMmPerHour ** alpha, k, alpha, model: 'ITU-R P.838-3' };
}

export interface RainfallProvider {
  readonly name: string;
  getRainRateMmPerHour(lat: number, lon: number): Promise<number>;
}
